import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Download, ExternalLink, Star } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSettings } from "../../context/SettingsContext";
import { Wordmark } from "../../components/Wordmark";
import { NerdBadge } from "../../components/NerdBadge";
import { GuideEditor } from "./GuideEditor";

const TABS = ["Setups", "Subscribers", "Settings"];

export default function AdminDashboard() {
  const { logout } = useAuth();
  const { settings, refresh } = useSettings();
  const [tab, setTab] = useState("Setups");
  const [guides, setGuides] = useState([]);
  const [editing, setEditing] = useState(null); // guide object or "new" or null
  const [subs, setSubs] = useState({ count: 0, subscribers: [] });
  const [form, setForm] = useState(settings);

  const loadGuides = () => api.get("/guides").then((r) => setGuides(r.data));
  const loadSubs = () => api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => {});

  useEffect(() => { loadGuides(); loadSubs(); }, []);
  useEffect(() => { setForm(settings); }, [settings]);

  const del = async (slug) => {
    if (!window.confirm("Delete this setup? This can't be undone.")) return;
    try {
      await api.delete(`/admin/guides/${slug}`);
      toast.success("Setup deleted");
      loadGuides();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", form);
      await refresh();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    const rows = [["email", "source", "date"], ...subs.subscribers.map((s) => [s.email, s.source, s.date])];
    const csv = rows.map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "subscribers.csv";
    a.click();
  };

  return (
    <div className="min-h-screen bg-warm" data-testid="admin-dashboard">
      <header className="border-b border-navy/10 bg-bone">
        <div className="container-brand flex h-[68px] items-center justify-between">
          <div className="flex items-center gap-4">
            <Wordmark style={{ fontSize: 22 }} />
            <span className="font-sans text-[11px] font-bold uppercase tracking-[0.16em] text-text-label">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="prose-link font-sans text-[12px] font-bold uppercase tracking-[0.12em]">View site</Link>
            <button onClick={logout} className="btn btn-secondary !py-2 !px-4 text-[13px]" data-testid="admin-logout"><LogOut size={15} /> Log out</button>
          </div>
        </div>
      </header>

      <div className="container-brand py-10">
        <div className="mb-8 flex gap-2 border-b border-navy/10">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`font-sans -mb-px border-b-2 px-4 py-3 text-[13px] font-bold uppercase tracking-[0.12em] transition-colors ${
                tab === t ? "border-ochre text-navy" : "border-transparent text-text-muted hover:text-navy"
              }`}
              data-testid={`admin-tab-${t.toLowerCase()}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* SETUPS */}
        {tab === "Setups" && (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-2xl">{guides.length} setups</h2>
              <button onClick={() => setEditing("new")} className="btn btn-primary" data-testid="admin-new-guide"><Plus size={16} /> New setup</button>
            </div>
            <div className="space-y-3">
              {guides.map((g) => (
                <div key={g.slug} className="brand-card flex flex-wrap items-center gap-4 p-4" data-testid={`admin-guide-${g.slug}`}>
                  <span className="font-sans inline-flex items-center rounded-md bg-navy px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-bone">{g.comment_word}</span>
                  <div className="flex-1 min-w-[180px]">
                    <span className="flex items-center gap-2 text-[17px] font-semibold text-navy">
                      {g.title}
                      {g.featured && <Star size={14} className="fill-ochre text-ochre" title="Featured" />}
                    </span>
                    <span className="text-[13px] text-text-muted">{g.category}{g.start_order != null ? ` · Start #${g.start_order}` : ""}</span>
                  </div>
                  <NerdBadge difficulty={g.difficulty} />
                  <div className="flex items-center gap-2">
                    <Link to={`/setups/${g.slug}`} target="_blank" className="p-2 text-text-muted hover:text-teal" title="View"><ExternalLink size={17} /></Link>
                    <button onClick={() => setEditing(g)} className="p-2 text-text-muted hover:text-navy" title="Edit" data-testid={`edit-${g.slug}`}><Pencil size={17} /></button>
                    <button onClick={() => del(g.slug)} className="p-2 text-text-muted hover:text-brick" title="Delete" data-testid={`delete-${g.slug}`}><Trash2 size={17} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SUBSCRIBERS */}
        {tab === "Subscribers" && (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-2xl">{subs.count} subscribers</h2>
              <button onClick={exportCsv} className="btn btn-secondary" disabled={!subs.count} data-testid="export-csv"><Download size={16} /> Export CSV</button>
            </div>
            <div className="brand-card overflow-hidden">
              <table className="w-full text-left text-[15px]">
                <thead className="bg-navy text-bone">
                  <tr className="font-sans text-[11px] uppercase tracking-[0.12em]">
                    <th className="px-5 py-3">Email</th>
                    <th className="px-5 py-3">Source</th>
                    <th className="px-5 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.subscribers.map((s) => (
                    <tr key={s.id} className="border-t border-navy/10">
                      <td className="px-5 py-3 text-navy">{s.email}</td>
                      <td className="px-5 py-3 text-text-muted">{s.source}</td>
                      <td className="px-5 py-3 text-text-muted">{new Date(s.date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {subs.count === 0 && (
                    <tr><td colSpan={3} className="px-5 py-8 text-center text-text-muted">No subscribers yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SETTINGS */}
        {tab === "Settings" && (
          <form onSubmit={saveSettings} className="max-w-xl space-y-5" data-testid="admin-settings-form">
            <h2 className="text-2xl">Site settings</h2>
            <div>
              <label className="field-label">YouTube URL</label>
              <input className="brand-input" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} data-testid="settings-youtube" />
            </div>
            <div>
              <label className="field-label">TikTok URL</label>
              <input className="brand-input" value={form.tiktok_url} onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })} data-testid="settings-tiktok" />
            </div>
            <div>
              <label className="field-label">Tagline</label>
              <input className="brand-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="settings-tagline" />
            </div>
            <div>
              <label className="field-label">Support line</label>
              <input className="brand-input" value={form.support_line} onChange={(e) => setForm({ ...form, support_line: e.target.value })} data-testid="settings-support" />
            </div>
            <button type="submit" className="btn btn-primary" data-testid="settings-save">Save settings</button>
          </form>
        )}
      </div>

      {editing && (
        <GuideEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadGuides(); }}
        />
      )}
    </div>
  );
}
